import { PrinterProfile } from '../../types/printerProfile';
import { PortedTicketData as TicketData } from './types';
import { buildTicketPlan, renderNativeColumns, joinTicketLines } from './ticketTestPlan';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * TEST 3 — « ANTI-MOTIF » : l'ancre est ARMÉE, mais la quantité n'est jamais `x2`
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * C'est le CONTRE-TEST exact du TEST 4. Là où le TEST 4 désarme l'ancre et assume le
 * motif, celui-ci fait l'inverse : il émet volontairement la ligne `ARTICLES` — donc
 * `inArticlesSection` est ARMÉ, tout le pré-traitement natif est actif — mais aucune
 * ligne ne peut matcher la regex de destruction.
 *
 * `MainActivity:4826` :
 *
 *     else if (inArticlesSection && cleanedLine.matches(".*x\\d+.*")) { … prix détruit … }
 *
 * La regex Java exige un `x` MINUSCULE et ASCII immédiatement suivi d'un chiffre. Donc
 * `2× Pizza Margherita` (avec `×` = U+00D7, présent en CP1252, donc imprimable après
 * `getBytes("windows-1252")`) ne matche PAS, et la ligne repart intacte dans la branche
 * `else` — colonne des prix comprise.
 *
 * CE QUE CE MODULE PROUVE : que le verrou est déclenché par le MOTIF et non par la
 * présence de deux colonnes. Si TEST 3 sort avec ses prix alors qu'un ticket `legacy`
 * classique les perd, le diagnostic est confirmé sans ambiguïté.
 *
 * ⚠️ SI TEST 4 SORT CORRECT, TEST 3 EST INUTILE en production : désarmer l'ancre coûte
 * une ligne, réécrire toutes les quantités coûte une convention d'affichage. Les deux
 * sont ici pour DÉPARTAGER, pas pour cohabiter.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * CE QUE L'ANCRE ARMÉE PRODUIT QUAND MÊME (attendu, pas un bug)
 * ───────────────────────────────────────────────────────────────────────────────
 * La ligne `ARTICLES` est REMPLACÉE par le natif (MainActivity:4807-4810) par :
 *     [C]* * * * * * * * * * * * * * * *
 *     [C]- ARTICLES -
 *     [C]* * * * * * * * * * * * * * * *
 * C'est le SIGNAL VISUEL que l'ancre a bien été armée : si ces trois lignes n'apparaissent
 * pas sur le papier, c'est que le ticket n'est pas passé par `getAsyncEscPosPrinter()`
 * (Star ou Sunmi) et le test ne vaut rien.
 *
 * Les étoiles ne contiennent AUCUN tiret : le piège de dédoublonnage (deux lignes
 * identiques consécutives avalées dès que le tampon contient 24 tirets) n'est donc pas
 * armé par ce module — un article commandé deux fois de suite reste imprimé deux fois.
 *
 * ⚠️ `killXPattern` couvre aussi les NOMS DE PRODUITS : « Box 2x50cl » réarmerait la
 * réécriture et fausserait la conclusion. Idem pour `taille:`, réécrit par une autre
 * branche du même bloc (`MainActivity:4837`).
 *
 * ⚠️ CIBLES : chemin ESC/POS uniquement, comme le TEST 4.
 */

const widthOf = (profile?: PrinterProfile): number => {
  const w = Number(profile?.config?.width);
  return Number.isFinite(w) && w >= 24 ? Math.min(64, Math.floor(w)) : 48;
};

export const buildAntiPatternTicket = (data: TicketData, profile?: PrinterProfile): string => {
  const width = widthOf(profile);
  const emitMarkers = profile?.config?.showCategories !== false;

  const plan = buildTicketPlan(data, {
    width,
    // `2× Nom` : le seul « x » que la regex Java ne voit pas.
    quantityStyle: 'multiply-prefix',
    emitCategoryMarkers: emitMarkers,
  });

  return joinTicketLines(
    renderNativeColumns(plan.lines, plan.width, {
      // ANCRE ARMÉE : ligne nue `ARTICLES`, sans la moindre balise.
      anchorMode: 'bare',
      killXPattern: true,
      emitMarkers,
    })
  );
};
